import { motion } from 'framer-motion';
import type { MarkupNode } from '@/engine/narrative';
import { cn } from '@/utils/cn';

const GLOW_COLORS: Record<string, string> = {
  ember: 'text-ember-300 drop-shadow-[0_0_6px_rgba(243,134,64,0.75)]',
  mystic: 'text-mystic-300 drop-shadow-[0_0_6px_rgba(143,152,207,0.75)]',
  moss: 'text-moss-300 drop-shadow-[0_0_6px_rgba(122,171,102,0.75)]',
  ash: 'text-ash-200 drop-shadow-[0_0_6px_rgba(255,255,255,0.45)]',
};

/**
 * Renderiza un único nodo de markup. Respeta una cantidad máxima
 * de caracteres visibles (para el typewriter); los demás se ocultan
 * con visibility: hidden para preservar el layout.
 */
export function MarkupSpan({
  node,
  maxChars,
}: {
  node: MarkupNode;
  maxChars: number;
}) {
  if (node.type === 'text') {
    return <>{renderCaracteres(node.value, maxChars, 'text-ash-100')}</>;
  }

  const clases = claseParaTag(node);
  const contenido = renderCaracteres(node.value, maxChars, '');

  switch (node.name) {
    case 'shake':
      return (
        <motion.span
          className={clases}
          animate={{ x: [0, -1, 1.5, -2, 2, -1, 0] }}
          transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 0.8 }}
        >
          {contenido}
        </motion.span>
      );
    case 'glow':
      return <span className={cn('animate-glow-pulse', clases)}>{contenido}</span>;
    case 'fade':
      return (
        <motion.span
          className={clases}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.8, ease: 'easeOut' }}
        >
          {contenido}
        </motion.span>
      );
    default:
      return <span className={clases}>{contenido}</span>;
  }
}

function claseParaTag(node: Extract<MarkupNode, { type: 'tag' }>): string {
  switch (node.name) {
    case 'shake':
      return 'inline-block';
    case 'glow': {
      const color = node.attrs.color ?? 'ember';
      return GLOW_COLORS[color] ?? GLOW_COLORS.ember;
    }
    case 'fade':
      return 'inline';
    case 'emph':
      return 'italic text-ash-50';
    case 'whisper':
      return 'text-sm text-ash-400 italic';
    case 'pipo':
      return 'italic text-moss-300';
    case 'typewriter':
      // La velocidad custom se maneja a nivel párrafo; acá solo marcamos.
      return 'inline';
  }
}

/**
 * Renderiza hasta `maxChars` caracteres de `texto`. El resto ocupa
 * el espacio con visibility: hidden para que el layout no salte
 * cuando el typewriter revele el resto.
 */
function renderCaracteres(texto: string, maxChars: number, claseExtra: string) {
  if (maxChars >= texto.length) {
    return <span className={claseExtra}>{texto}</span>;
  }
  const visible = texto.slice(0, Math.max(0, maxChars));
  const oculto = texto.slice(Math.max(0, maxChars));
  return (
    <span className={claseExtra}>
      {visible}
      <span className="invisible" aria-hidden>
        {oculto}
      </span>
    </span>
  );
}
